import React, { useState } from 'react';
import { useI18n } from './i18n';
import { authFetch, isAuthenticated } from './auth';
import LabelScanner, { mergeScanValues } from './LabelScanner';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

const FIELDS = ['name', 'brewery', 'style', 'abv', 'volume_ml', 'ibu'];

const toForm = (beer) => Object.fromEntries(FIELDS.map((f) => [f, beer && beer[f] != null ? String(beer[f]) : '']));

const num = (value, integer) => {
  if (value === '' || value == null) return null;
  const n = Number(String(value).replace(',', '.'));
  if (Number.isNaN(n)) return null;
  return integer ? Math.round(n) : n;
};

// Create or edit a beer of the catalog. New beers can be filled from a label photo (OCR); a logged-in user's
// photo is also kept as the beer's label. Props: beer (to edit), onDone(beer), onCancel().
function BeerForm({ beer, onDone, onCancel }) {
  const { t } = useI18n();
  const editing = !!beer;
  const [values, setValues] = useState(() => toForm(beer));
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const set = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));

  const applyScan = (scanned) => {
    const text = {};
    Object.entries(scanned || {}).forEach(([k, v]) => { if (FIELDS.includes(k) && v != null) text[k] = String(v); });
    setValues((current) => mergeScanValues(current, text));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name.trim()) {
      setMessage({ type: 'error', text: t('beers.form.name_required') });
      return;
    }
    setSaving(true);
    setMessage(null);
    const body = {
      name: values.name.trim(), brewery: values.brewery.trim() || null, style: values.style.trim() || null,
      abv: num(values.abv), volume_ml: num(values.volume_ml, true), ibu: num(values.ibu, true),
    };
    try {
      const res = await authFetch(`${API_URL}/beers${editing ? `/${beer.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) {
        setMessage({ type: 'error', text: data.detail || t('beers.form.error_save') });
        return;
      }
      if (!editing && photo && isAuthenticated()) {          // keep the scanned photo as the label; failures are not shown
        try {
          const upload = new FormData();
          upload.append('file', photo);
          await authFetch(`${API_URL}/beers/${data.id}/attachments`, { method: 'POST', body: upload });
        } catch { /* the beer is saved anyway */ }
      }
      onDone(data);
    } catch {
      setMessage({ type: 'error', text: t('beers.form.error_save') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="sheet-form" onSubmit={submit}>
      <h2>{t(editing ? 'beers.form.title_edit' : 'beers.form.title_new')}</h2>
      {!editing && <LabelScanner kind="beer" onApply={applyScan} onPhoto={setPhoto} />}
      <div className="form-field">
        <label>{t('beers.name')} *</label>
        <input value={values.name} onChange={set('name')} maxLength={200} required />
      </div>
      <div className="form-grid">
        <div className="form-field">
          <label>{t('beers.brewery')}</label>
          <input value={values.brewery} onChange={set('brewery')} maxLength={200} />
        </div>
        <div className="form-field">
          <label>{t('beers.style')}</label>
          <input value={values.style} onChange={set('style')} maxLength={100} />
        </div>
      </div>
      <div className="form-grid">
        <div className="form-field">
          <label>{t('beers.abv')}</label>
          <input type="number" inputMode="decimal" step="0.1" min="0" max="100" value={values.abv} onChange={set('abv')} />
        </div>
        <div className="form-field">
          <label>{t('beers.volume')}</label>
          <input type="number" inputMode="numeric" step="1" min="20" max="20000" value={values.volume_ml} onChange={set('volume_ml')} />
        </div>
        <div className="form-field">
          <label>{t('beers.ibu')}</label>
          <input type="number" inputMode="numeric" step="1" min="0" max="300" value={values.ibu} onChange={set('ibu')} />
        </div>
      </div>
      {!editing && !isAuthenticated() && <small className="import-hint">{t('beers.form.photo_login_hint')}</small>}
      {message && <div className={`message ${message.type}`}>{message.text}</div>}
      <div className="form-buttons">
        <button type="submit" className="submit-btn" disabled={saving}>{t(editing ? 'beers.form.save_edit' : 'beers.form.save')}</button>
        <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
      </div>
    </form>
  );
}

export default BeerForm;
